/**
 * Speed Variation Utilities for Enhanced Race Animation
 *
 * This utility provides functions for calculating speed multipliers, applying easing
 * functions for smooth transitions, and managing timing for pattern execution.
 */

import { SpeedSegment, RacingPattern } from "./racingPatternGenerator";

// Easing function type definition
export type EasingFunction = (t: number) => number;

// Interface for speed calculation result
export interface SpeedCalculationResult {
  speedMultiplier: number;
  easedProgress: number;
  segmentProgress: number;
  currentSegment: SpeedSegment | null;
}

// Interface for timing utilities configuration
export interface TimingConfig {
  baseSpeed: number; // Base movement speed in pixels per second
  frameRate: number; // Target frame rate for smooth animations
}

// Default timing configuration
const DEFAULT_TIMING_CONFIG: TimingConfig = {
  baseSpeed: 150, // 150 pixels per second base speed
  frameRate: 60, // 60 FPS for smooth animation
};

/**
 * Easing Functions
 * These provide smooth transitions between speed segments
 */
export const EasingFunctions: Record<SpeedSegment["easing"], EasingFunction> = {
  linear: (t: number): number => t,

  "ease-in": (t: number): number => t * t,

  "ease-out": (t: number): number => 1 - Math.pow(1 - t, 2),

  "ease-in-out": (t: number): number => {
    if (t < 0.5) {
      return 2 * t * t;
    }
    return 1 - Math.pow(-2 * t + 2, 2) / 2;
  },
};

/**
 * Calculates the progress within a time segment (0 to 1)
 */
export function calculateSegmentProgress(
  currentTime: number,
  startTime: number,
  endTime: number
): number {
  if (endTime <= startTime) {
    return 1; // Avoid division by zero
  }

  const progress = (currentTime - startTime) / (endTime - startTime);
  return Math.max(0, Math.min(1, progress)); // Clamp between 0 and 1
}

/**
 * Applies easing function to a progress value
 */
export function applyEasing(
  progress: number,
  easingType: SpeedSegment["easing"]
): number {
  const easingFunction = EasingFunctions[easingType];
  return easingFunction(progress);
}

/**
 * Finds the current speed segment for a given time
 */
export function findCurrentSegment(
  pattern: RacingPattern,
  currentTime: number
): SpeedSegment | null {
  // Find all segments that contain the current time
  const matchingSegments = pattern.speedVariations.filter(
    (segment) =>
      currentTime >= segment.startTime && currentTime <= segment.endTime
  );

  if (matchingSegments.length === 0) {
    return null;
  }

  // If multiple segments match (at boundary), prefer the one that starts at currentTime
  const segmentStartingAtTime = matchingSegments.find(
    (segment) => segment.startTime === currentTime
  );

  return segmentStartingAtTime || matchingSegments[0];
}

// Cache for segment lookups to improve performance
const segmentCache = new Map<
  string,
  { segment: SpeedSegment | null; time: number }
>();

/**
 * Optimized segment finder with caching
 */
function findCurrentSegmentOptimized(
  pattern: RacingPattern,
  currentTime: number
): SpeedSegment | null {
  // Generate cache key
  const cacheKey = `${pattern.competitorId}-${currentTime}`;
  const cached = segmentCache.get(cacheKey);

  // Return cached result if it's recent (within 50ms)
  if (cached && Math.abs(cached.time - currentTime) < 50) {
    return cached.segment;
  }

  // Find segment using binary search for better performance with many segments
  const segments = pattern.speedVariations;
  if (segments.length === 0) {
    return null;
  }

  // Find all segments that contain the current time
  const matchingSegments = segments.filter(
    (segment) =>
      currentTime >= segment.startTime && currentTime <= segment.endTime
  );

  if (matchingSegments.length === 0) {
    return null;
  }

  // If multiple segments match (at boundary), prefer the one that starts at currentTime
  const segmentStartingAtTime = matchingSegments.find(
    (segment) => segment.startTime === currentTime
  );

  const result = segmentStartingAtTime || matchingSegments[0];

  // Cache the result but limit cache size
  if (segmentCache.size < 50) {
    segmentCache.set(cacheKey, { segment: result, time: currentTime });
  }

  return result;
}

/**
 * Calculates the current speed multiplier with easing applied (optimized)
 */
export function calculateCurrentSpeedMultiplier(
  pattern: RacingPattern,
  currentTime: number
): SpeedCalculationResult {
  const currentSegment = findCurrentSegmentOptimized(pattern, currentTime);

  if (!currentSegment) {
    return {
      speedMultiplier: 1.0, // Default base speed
      easedProgress: 0,
      segmentProgress: 0,
      currentSegment: null,
    };
  }

  // Calculate progress within the current segment (optimized)
  const duration = currentSegment.endTime - currentSegment.startTime;
  const segmentProgress =
    duration > 0
      ? Math.max(
          0,
          Math.min(1, (currentTime - currentSegment.startTime) / duration)
        )
      : 1;

  // Apply easing to the progress
  const easedProgress = applyEasing(segmentProgress, currentSegment.easing);

  // Use the segment's speed multiplier directly for better performance
  const speedMultiplier = currentSegment.speedMultiplier;

  return {
    speedMultiplier,
    easedProgress,
    segmentProgress,
    currentSegment,
  };
}

/**
 * Clears the segment cache (useful for testing or memory management)
 */
export function clearSegmentCache(): void {
  segmentCache.clear();
}

/**
 * Calculates the distance that should be traveled in a given time frame
 */
export function calculateDistanceForTimeFrame(
  speedMultiplier: number,
  timeFrameMs: number,
  config: Partial<TimingConfig> = {}
): number {
  const { baseSpeed } = { ...DEFAULT_TIMING_CONFIG, ...config };

  // Convert milliseconds to seconds
  const timeFrameSeconds = timeFrameMs / 1000;

  // Calculate distance: speed * time
  const distance = baseSpeed * speedMultiplier * timeFrameSeconds;

  return Math.round(distance * 100) / 100; // Round to 2 decimal places
}

/**
 * Calculates the time needed to travel a specific distance
 */
export function calculateTimeForDistance(
  distance: number,
  speedMultiplier: number,
  config: Partial<TimingConfig> = {}
): number {
  const { baseSpeed } = { ...DEFAULT_TIMING_CONFIG, ...config };

  if (speedMultiplier <= 0) {
    return Infinity; // Can't travel if speed is zero or negative
  }

  // Calculate time: distance / speed
  const timeSeconds = distance / (baseSpeed * speedMultiplier);

  // Convert to milliseconds
  return Math.round(timeSeconds * 1000);
}

/**
 * Interpolates between two speed multipliers for smooth transitions
 */
export function interpolateSpeedMultipliers(
  fromSpeed: number,
  toSpeed: number,
  progress: number,
  easingType: SpeedSegment["easing"] = "linear"
): number {
  const easedProgress = applyEasing(progress, easingType);
  const interpolated = fromSpeed + (toSpeed - fromSpeed) * easedProgress;

  return Math.round(interpolated * 100) / 100; // Round to 2 decimal places
}

/**
 * Calculates the optimal frame interval for smooth animation
 */
export function calculateFrameInterval(
  config: Partial<TimingConfig> = {}
): number {
  const { frameRate } = { ...DEFAULT_TIMING_CONFIG, ...config };
  return Math.round(1000 / frameRate); // Convert FPS to milliseconds per frame
}

/**
 * Generates timing checkpoints for a racing pattern
 * These can be used to pre-calculate animation keyframes
 */
export function generateTimingCheckpoints(
  pattern: RacingPattern,
  intervalMs: number = 100
): Array<{
  time: number;
  speedMultiplier: number;
  segmentIndex: number;
}> {
  const checkpoints: Array<{
    time: number;
    speedMultiplier: number;
    segmentIndex: number;
  }> = [];

  if (pattern.speedVariations.length === 0) {
    return checkpoints;
  }

  const totalTime = Math.max(
    ...pattern.speedVariations.map((segment) => segment.endTime)
  );

  for (let time = 0; time <= totalTime; time += intervalMs) {
    const result = calculateCurrentSpeedMultiplier(pattern, time);
    const segmentIndex = pattern.speedVariations.findIndex(
      (segment) => segment === result.currentSegment
    );

    checkpoints.push({
      time,
      speedMultiplier: result.speedMultiplier,
      segmentIndex: segmentIndex >= 0 ? segmentIndex : -1,
    });
  }

  return checkpoints;
}

/**
 * Validates speed variation parameters
 */
export function validateSpeedVariation(
  speedMultiplier: number,
  minSpeed: number = 0.1,
  maxSpeed: number = 3.0
): boolean {
  return (
    typeof speedMultiplier === "number" &&
    !isNaN(speedMultiplier) &&
    speedMultiplier >= minSpeed &&
    speedMultiplier <= maxSpeed
  );
}

/**
 * Smooths out speed variations to prevent jarring transitions
 */
export function smoothSpeedTransitions(
  segments: SpeedSegment[],
  maxSpeedChange: number = 0.5
): SpeedSegment[] {
  if (segments.length <= 1) {
    return [...segments];
  }

  const smoothedSegments = [...segments];

  for (let i = 1; i < smoothedSegments.length; i++) {
    const prevSegment = smoothedSegments[i - 1];
    const currentSegment = smoothedSegments[i];

    const speedDifference = Math.abs(
      currentSegment.speedMultiplier - prevSegment.speedMultiplier
    );

    // If the speed change is too dramatic, smooth it out
    if (speedDifference > maxSpeedChange) {
      const direction =
        currentSegment.speedMultiplier > prevSegment.speedMultiplier ? 1 : -1;
      smoothedSegments[i] = {
        ...currentSegment,
        speedMultiplier:
          prevSegment.speedMultiplier + maxSpeedChange * direction,
      };
    }
  }

  return smoothedSegments;
}

/**
 * Calculates the total race distance based on speed patterns
 */
export function calculateTotalRaceDistance(
  pattern: RacingPattern,
  config: Partial<TimingConfig> = {}
): number {
  let totalDistance = 0;

  for (const segment of pattern.speedVariations) {
    const segmentDuration = segment.endTime - segment.startTime;
    const segmentDistance = calculateDistanceForTimeFrame(
      segment.speedMultiplier,
      segmentDuration,
      config
    );
    totalDistance += segmentDistance;
  }

  return Math.round(totalDistance);
}
